// Iter124j · Constructor de Nueva Cotización (una sola pantalla, sin wizard)
// Ruta real: /panel/cotizar y /panel/cotizar/:id (editar)
// Reutiliza: campos backend Iter124h (total_calculado, total_pactado, usar_total_pactado, total_contratado, secciones, iva, iva_tasa, descuento)
import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API, BACKEND_URL, useAuth } from "../../App";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Eye, Download, MoreVertical, Plus, Trash2, Copy,
  ChevronUp, ChevronDown, Package, Edit3, Search, X, GripVertical, CalendarPlus
} from "lucide-react";

const UNIDADES = ["Persona","Adulto","Niño","Hora","Mesa","Pieza","Paquete","Día","Servicio","Kg","Litro","Otro"];
const MODALIDADES = [
  { id: "cantidad_x_precio", label: "Cantidad × precio" },
  { id: "por_persona", label: "Por persona" },
  { id: "por_adulto", label: "Por adulto" },
  { id: "por_nino", label: "Por niño" },
  { id: "por_hora", label: "Por hora" },
  { id: "por_mesa", label: "Por mesa" },
  { id: "por_pieza", label: "Por pieza" },
  { id: "por_dia", label: "Por día" },
  { id: "precio_fijo", label: "Precio fijo / paquete" },
  { id: "importe_manual", label: "Importe manual" },
];

const fmt = (v) => "$" + Number(v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const calcularImporte = (c, adultos, ninos) => {
  const cant = Number(c.cantidad || 0);
  const precio = Number(c.precio_unitario || 0);
  switch (c.modalidad) {
    case "por_persona": return precio * (Number(adultos||0) + Number(ninos||0));
    case "por_adulto": return precio * Number(adultos||0);
    case "por_nino": return precio * Number(ninos||0);
    case "por_hora":
    case "por_mesa":
    case "por_pieza":
    case "por_dia":
    case "cantidad_x_precio": return cant * precio;
    case "precio_fijo": return precio;
    case "importe_manual": return Number(c.importe || 0);
    default: return cant * precio;
  }
};

const NuevaCotizacion = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id: cotId } = useParams();
  const isEdit = Boolean(cotId);

  useEffect(() => {
    const ws = (user?.workspace_rol || "").toLowerCase();
    if (["mesero","chef","cliente"].includes(ws)) {
      toast.error("Tu rol no tiene permiso para cotizar.");
      navigate("/panel/mi-panel", { replace: true });
    }
  }, [user, navigate]);

  // Cliente y evento (una sola tarjeta)
  const [clienteId, setClienteId] = useState("");
  const [tipoEvento, setTipoEvento] = useState("");
  const [festejado, setFestejado] = useState("");
  const [fecha, setFecha] = useState("");
  const [horaInicio, setHoraInicio] = useState("");
  const [horaFin, setHoraFin] = useState("");
  const [ubicacion, setUbicacion] = useState("");
  const [adultos, setAdultos] = useState(0);
  const [ninos, setNinos] = useState(0);
  const [otros, setOtros] = useState(0);

  // Estructura del constructor
  const [secciones, setSecciones] = useState([]);
  const [descuento, setDescuento] = useState(0);
  const [iva, setIva] = useState(true);
  const [ivaTasa, setIvaTasa] = useState(16);
  // Iter124r · P1 · Identidad comercial (marca) presentada al cliente
  const [identidades, setIdentidades] = useState([]);
  const [identidadId, setIdentidadId] = useState("");
  const [tiposEvento, setTiposEvento] = useState([]);
  const [usarPactado, setUsarPactado] = useState(false);
  const [totalPactado, setTotalPactado] = useState(0);
  const [anticipo, setAnticipo] = useState(0);

  const [clientes, setClientes] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [saving, setSaving] = useState(false);
  const [estatus, setEstatus] = useState("borrador");
  const [snapshotAt, setSnapshotAt] = useState(null);
  const [modalConcepto, setModalConcepto] = useState(null); // { seccionIdx, conceptoIdx? }
  const [modalCatalogo, setModalCatalogo] = useState(null); // { seccionIdx }
  const [modalCliente, setModalCliente] = useState(false);
  const [nuevoCliNombre, setNuevoCliNombre] = useState("");
  const [nuevoCliTelefono, setNuevoCliTelefono] = useState("");
  // Iter124m · Menú desplegable "⋮" (móvil especialmente): fix del botón muerto.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    const onDocClick = (e) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
    };
  }, [menuOpen]);

  // Cargar datos base
  useEffect(() => {
    (async () => {
      try {
        const [cs, cat, idns, tps] = await Promise.all([
          axios.get(`${API}/clientes`).catch(() => ({ data: [] })),
          axios.get(`${API}/catalogo/unificado`).catch(() => ({ data: [] })),
          axios.get(`${API}/identidades-comerciales`).catch(() => ({ data: [] })),
          axios.get(`${API}/tipos-evento`).catch(() => ({ data: [] })),
        ]);
        setClientes(cs.data || []);
        setCatalogo(cat.data || []);
        setTiposEvento(Array.isArray(tps.data) ? tps.data : []);
        const idnList = (idns.data || []).filter(x => x.activo !== false);
        setIdentidades(idnList);
        // Preseleccionar default si no es edición
        if (!isEdit) {
          const def = idnList.find(x => x.es_default) || idnList[0];
          if (def) setIdentidadId(def.identidad_id);
        }
      } catch (e) { /* silencioso */ }
    })();
  }, [isEdit]);

  // Cargar cotización si es edición
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/cotizaciones/${cotId}`);
        const c = data.cotizacion || data;
        setClienteId(c.cliente_id || ""); setTipoEvento(c.tipo_evento || "");
        setFestejado(c.festejado || c.nombre_festejado || ""); setFecha(c.fecha_evento || "");
        setHoraInicio(c.hora_inicio || ""); setHoraFin(c.hora_fin || ""); setUbicacion(c.lugar || "");
        setAdultos(c.adultos || 0); setNinos(c.ninos || 0); setOtros(c.otros || c.jovenes || 0);
        // Legacy: si tiene items pero no secciones, meterlos en una sección "Servicios"
        let secs = c.secciones && c.secciones.length ? c.secciones : null;
        if (!secs) {
          const legacy = c.items || c.servicios || [];
          if (legacy.length) secs = [{ id: `sec_${Date.now()}`, nombre: "Servicios", items: legacy, expandido: true }];
        }
        setSecciones(secs || []);
        setDescuento(c.descuento || 0); setIva(!!c.iva); setIvaTasa(c.iva_tasa || 16);
        setUsarPactado(!!c.usar_total_pactado); setTotalPactado(c.total_pactado || 0);
        setAnticipo(c.anticipo || 0); setEstatus(c.estatus || "borrador"); setSnapshotAt(c.snapshot_at || null);
        if (c.identidad_comercial_id) setIdentidadId(c.identidad_comercial_id);
      } catch (e) { toast.error("No se pudo cargar la cotización"); }
    })();
  }, [cotId, isEdit]);

  // Totales (misma fórmula que backend Iter124h)
  const totales = useMemo(() => {
    let subtotal = 0;
    secciones.forEach(sec => (sec.items || []).forEach(it => {
      const clas = (it.clasificacion || "incluido").toLowerCase();
      if (clas === "opcional" && !it.seleccionado) return;
      if (clas === "adicional" && it.aceptado === false) return;
      subtotal += calcularImporte(it, adultos, ninos);
    }));
    const base = Math.max(subtotal - Number(descuento||0), 0);
    const ivaMonto = iva ? +(base * (Number(ivaTasa||0)/100)).toFixed(2) : 0;
    const totalCalc = +(base + ivaMonto).toFixed(2);
    const totalContr = usarPactado && totalPactado ? Number(totalPactado) : totalCalc;
    return { subtotal, descuento: Number(descuento||0), ivaMonto, totalCalc, totalContr, saldo: Math.max(totalContr - Number(anticipo||0), 0) };
  }, [secciones, descuento, iva, ivaTasa, usarPactado, totalPactado, anticipo, adultos, ninos]);

  // Acciones sobre secciones
  const addSeccion = () => setSecciones([...secciones, { id: `sec_${Date.now()}`, nombre: `Sección ${secciones.length+1}`, items: [], expandido: true }]);
  const updateSeccion = (idx, patch) => setSecciones(secciones.map((s,i) => i===idx ? {...s, ...patch} : s));
  const moveSeccion = (idx, dir) => {
    const j = idx + dir; if (j < 0 || j >= secciones.length) return;
    const next = [...secciones]; [next[idx], next[j]] = [next[j], next[idx]]; setSecciones(next);
  };
  const duplicarSeccion = (idx) => {
    const s = secciones[idx];
    setSecciones([...secciones.slice(0, idx+1), { ...s, id: `sec_${Date.now()}`, nombre: `${s.nombre} (copia)` }, ...secciones.slice(idx+1)]);
  };
  const removeSeccion = (idx) => {
    if (!window.confirm("¿Eliminar esta sección y sus conceptos?")) return;
    setSecciones(secciones.filter((_,i) => i!==idx));
  };

  // Concepto
  const saveConcepto = (seccionIdx, concepto, conceptoIdx) => {
    const s = {...secciones[seccionIdx]};
    const items = [...(s.items||[])];
    concepto.importe = calcularImporte(concepto, adultos, ninos);
    if (conceptoIdx == null) items.push({ ...concepto, id: `it_${Date.now()}` });
    else items[conceptoIdx] = { ...items[conceptoIdx], ...concepto };
    s.items = items; updateSeccion(seccionIdx, s); setModalConcepto(null);
  };
  const duplicarConcepto = (si, ci) => {
    const s = {...secciones[si]}; const it = {...s.items[ci], id: `it_${Date.now()}`};
    s.items = [...s.items.slice(0, ci+1), it, ...s.items.slice(ci+1)]; updateSeccion(si, s);
  };
  const moverConcepto = (si, ci, dir) => {
    const s = {...secciones[si]}; const j = ci+dir; if (j<0 || j>=s.items.length) return;
    const next=[...s.items]; [next[ci],next[j]]=[next[j],next[ci]]; s.items=next; updateSeccion(si,s);
  };
  const eliminarConcepto = (si, ci) => {
    if (!window.confirm("¿Eliminar este concepto?")) return;
    const s = {...secciones[si]}; s.items = s.items.filter((_,i)=>i!==ci); updateSeccion(si,s);
  };
  const insertarDesdeCatalogo = (seccionIdx, catItem) => {
    const c = { id: `it_${Date.now()}`, nombre: catItem.nombre, descripcion: catItem.descripcion || "",
      cantidad: 1, unidad: catItem.unidad || "Servicio", modalidad: "cantidad_x_precio",
      precio_unitario: catItem.precio || catItem.precio_unitario || 0, clasificacion: "incluido", seleccionado: true };
    c.importe = calcularImporte(c, adultos, ninos);
    const s = {...secciones[seccionIdx]}; s.items = [...(s.items||[]), c]; updateSeccion(seccionIdx, s); setModalCatalogo(null);
  };

  const guardar = async () => {
    if (!clienteId) { toast.error("Selecciona un cliente"); return; }
    setSaving(true);
    try {
      const payload = {
        cliente_id: clienteId, tipo_evento: tipoEvento, festejado, fecha_evento: fecha,
        hora_inicio: horaInicio, hora_fin: horaFin, lugar: ubicacion,
        adultos: Number(adultos), ninos: Number(ninos), jovenes: Number(otros),
        secciones, descuento: Number(descuento), iva: !!iva, iva_tasa: Number(ivaTasa),
        usar_total_pactado: !!usarPactado, total_pactado: Number(totalPactado||0),
        anticipo: Number(anticipo), estatus,
        identidad_comercial_id: identidadId || null,
      };
      if (isEdit) { await axios.put(`${API}/cotizaciones/${cotId}`, payload); toast.success("Cotización guardada"); }
      else { const { data } = await axios.post(`${API}/cotizaciones`, payload); toast.success("Cotización guardada"); navigate(`/panel/cotizar/${data.cotizacion_id}`, { replace: true }); }
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar. Tus cambios permanecen en pantalla."); }
    finally { setSaving(false); }
  };

  const crearCliente = async () => {
    if (!nuevoCliNombre.trim() || !nuevoCliTelefono.trim()) { toast.error("Nombre y teléfono son obligatorios"); return; }
    try {
      const { data } = await axios.post(`${API}/clientes`, { nombre: nuevoCliNombre, telefono: nuevoCliTelefono, whatsapp: nuevoCliTelefono, tipo_evento: tipoEvento || "Evento" });
      const id = data.cliente_id || (data.cliente||{}).cliente_id;
      toast.success("Cliente creado");
      const cs = await axios.get(`${API}/clientes`); setClientes(cs.data || []); setClienteId(id);
      setModalCliente(false); setNuevoCliNombre(""); setNuevoCliTelefono("");
    } catch (e) { toast.error("No se pudo crear el cliente"); }
  };

  // Iter124m · Acciones del menú "⋮"
  const duplicarCot = async () => {
    if (!isEdit) { toast.error("Guarda antes de duplicar."); return; }
    try {
      const { data } = await axios.post(`${API}/cotizaciones/${cotId}/duplicar`, {});
      toast.success("Cotización duplicada");
      const newId = data.cotizacion_id || (data.cotizacion||{}).cotizacion_id;
      if (newId) navigate(`/panel/cotizar/${newId}`);
    } catch { toast.error("No se pudo duplicar."); }
    finally { setMenuOpen(false); }
  };
  // Iter155 · Crear evento desde ESTA cotización (vínculo bidireccional).
  // Reutiliza `POST /api/eventos` (que ya persiste `cotizacion_id` y actualiza
  // `cotizaciones.evento_id`). No crea endpoints ni campos nuevos.
  const [creandoEvento, setCreandoEvento] = useState(false);
  const crearEventoDesdeCotizacion = async () => {
    if (!isEdit) { toast.error("Guarda la cotización antes de crear el evento."); return; }
    setCreandoEvento(true);
    try {
      // Fuente fresca (evita depender de estado local si la cot fue cargada con
      // otro esquema de campos). Mismo helper usado por el load.
      const { data: raw } = await axios.get(`${API}/cotizaciones/${cotId}`);
      const c = raw.cotizacion || raw;
      if (!c.cliente_id && !clienteId) {
        toast.error("La cotización no tiene cliente vinculado.");
        setCreandoEvento(false); return;
      }
      const identidad = identidades.find(i => i.identidad_id === identidadId || i.id === identidadId);
      const unidad_negocio = identidad?.unidad_negocio || identidad?.unidad_negocio_id || identidad?.nombre || undefined;
      // Mapear items reales (mismo shape usado por doc-templates)
      const secs = c.secciones || [];
      const servicios_incluidos = secs.flatMap(s =>
        (s.items || s.conceptos || []).filter(it => (it.nombre || "").trim()).map(it => ({
          nombre: it.nombre,
          categoria: s.nombre || "Servicios",
          cantidad: Number(it.cantidad) || 1,
          precio: Number(it.precio ?? it.precio_unitario ?? 0),
          total: Number(it.total ?? (Number(it.precio ?? it.precio_unitario ?? 0) * (Number(it.cantidad) || 1))),
          unidad: it.unidad || "",
        }))
      );
      const totalContr = Number(c.total_contratado ?? c.total ?? totales.totalContr) || 0;
      const payload = {
        cotizacion_id: cotId,
        cliente_id: c.cliente_id || clienteId,
        nombre: (c.festejado || c.nombre_festejado || c.tipo_evento || "Evento").toString().trim() || "Evento",
        tipo: c.tipo_evento || tipoEvento || "Evento",
        tipo_evento: c.tipo_evento || tipoEvento || "",
        festejado: c.festejado || c.nombre_festejado || festejado || "",
        nombre_festejado: c.festejado || c.nombre_festejado || festejado || "",
        fecha: c.fecha || c.fecha_evento || fecha || null,
        hora_inicio: c.hora_inicio || horaInicio || "",
        hora_fin: c.hora_fin || horaFin || "",
        ubicacion: c.lugar || c.ubicacion || ubicacion || "",
        adultos: Number(c.adultos ?? adultos) || 0,
        ninos: Number(c.ninos ?? ninos) || 0,
        otros: Number(c.otros ?? c.jovenes ?? otros) || 0,
        servicios_incluidos,
        total: totalContr,
        total_contratado: totalContr,
        anticipo: Number(c.anticipo ?? anticipo) || 0,
        identidad_comercial_id: c.identidad_comercial_id || identidadId || null,
      };
      if (unidad_negocio) payload.unidad_negocio = unidad_negocio;
      // Quitar strings vacíos y null (evita fallos de validación silenciosos)
      Object.keys(payload).forEach(k => { if (payload[k] === "" || payload[k] === null) delete payload[k]; });
      const { data } = await axios.post(`${API}/eventos`, payload);
      const eid = data?.evento_id || data?.evento?.evento_id;
      if (!eid) { toast.error("No se pudo obtener el evento creado."); return; }
      toast.success("Evento creado desde la cotización");
      setMenuOpen(false);
      navigate(`/panel/eventos/${eid}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.message || err?.message || "No se pudo crear el evento.";
      toast.error(typeof msg === "string" ? msg : "No se pudo crear el evento.");
    } finally {
      setCreandoEvento(false);
    }
  };
  const eliminarCot = async () => {
    if (!isEdit) { setMenuOpen(false); return; }
    if (!window.confirm("¿Eliminar esta cotización? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/cotizaciones/${cotId}`);
      toast.success("Cotización eliminada");
      navigate("/panel/cotizaciones");
    } catch { toast.error("No se pudo eliminar."); }
    finally { setMenuOpen(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--brand-surface)" }}>
      {/* Header sticky — Iter124m · Compacto en móvil, botones secundarios dentro de ⋮ */}
      <div className="sticky top-0 z-30 border-b" style={{ background: "var(--brand-surface-card)", borderColor: "var(--brand-border)" }}>
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-stone-100 shrink-0" data-testid="btn-volver"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-base sm:text-2xl truncate" style={{ color: "var(--brand-text)" }} data-testid="titulo-cot">
              {isEdit ? `Cotización ${cotId.slice(-6).toUpperCase()}` : "Nueva cotización"}
            </h1>
            {estatus && (
              <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: estatus==="aceptada"?"#dcfce7":"#fef3c7", color: estatus==="aceptada"?"#166534":"#92400e" }} data-testid="badge-estatus">
                {estatus}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <button onClick={guardar} disabled={saving} className="px-2.5 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium text-white disabled:opacity-50" style={{ background: "var(--brand-primary)" }} data-testid="btn-guardar-cot">
              <Save className="w-4 h-4 inline sm:mr-1" /><span className="hidden sm:inline">{saving ? "Guardando…" : "Guardar"}</span>
            </button>
            {isEdit && (
              <button
                onClick={crearEventoDesdeCotizacion}
                disabled={creandoEvento}
                className="hidden sm:inline-flex px-3 py-2 rounded-lg text-sm font-medium border items-center disabled:opacity-50"
                style={{ borderColor: "var(--brand-primary)", color: "var(--brand-primary)" }}
                data-testid="btn-crear-evento"
                title="Crear evento a partir de esta cotización"
              >
                <CalendarPlus className="w-4 h-4 mr-1" />{creandoEvento ? "Creando…" : "Crear evento"}
              </button>
            )}
            {isEdit && (
              <>
                <button onClick={() => window.open(`${BACKEND_URL}/api/cotizaciones/${cotId}/pdf/vista`, "_blank")} className="hidden sm:inline-flex px-3 py-2 rounded-lg text-sm border items-center" data-testid="btn-vista-previa"><Eye className="w-4 h-4 mr-1" />Vista previa</button>
                <a href={`${BACKEND_URL}/api/cotizaciones/${cotId}/pdf/final`} target="_blank" rel="noopener noreferrer" className="hidden sm:inline-flex px-3 py-2 rounded-lg text-sm border items-center" data-testid="btn-pdf"><Download className="w-4 h-4 mr-1" />PDF</a>
              </>
            )}
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen(v => !v)}
                className="p-2 rounded-lg border touch-manipulation active:bg-stone-100"
                title="Más acciones"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                data-testid="btn-mas"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-56 bg-white border rounded-lg shadow-lg z-50 py-1 text-sm"
                  data-testid="menu-mas"
                  style={{ borderColor: "var(--brand-border)" }}
                >
                  {isEdit && (
                    <>
                      <button
                        onClick={() => { setMenuOpen(false); crearEventoDesdeCotizacion(); }}
                        className="sm:hidden w-full text-left px-3 py-2 hover:bg-stone-50 flex items-center gap-2"
                        data-testid="menu-crear-evento"
                      ><CalendarPlus className="w-4 h-4" />Crear evento</button>
                      <button
                        onClick={() => { setMenuOpen(false); window.open(`${BACKEND_URL}/api/cotizaciones/${cotId}/pdf/vista`, "_blank"); }}
                        className="sm:hidden w-full text-left px-3 py-2 hover:bg-stone-50 flex items-center gap-2"
                        data-testid="menu-vista-previa"
                      ><Eye className="w-4 h-4" />Vista previa</button>
                      <button
                        onClick={() => { setMenuOpen(false); window.open(`${BACKEND_URL}/api/cotizaciones/${cotId}/pdf/final`, "_blank"); }}
                        className="sm:hidden w-full text-left px-3 py-2 hover:bg-stone-50 flex items-center gap-2"
                        data-testid="menu-pdf"
                      ><Download className="w-4 h-4" />Descargar PDF</button>
                      <div className="sm:hidden border-t my-1" />
                      <button
                        onClick={duplicarCot}
                        className="w-full text-left px-3 py-2 hover:bg-stone-50 flex items-center gap-2"
                        data-testid="menu-duplicar"
                      ><Copy className="w-4 h-4" />Duplicar cotización</button>
                      <button
                        onClick={eliminarCot}
                        className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-600 flex items-center gap-2"
                        data-testid="menu-eliminar"
                      ><Trash2 className="w-4 h-4" />Eliminar cotización</button>
                    </>
                  )}
                  {!isEdit && (
                    <div className="px-3 py-2 text-xs text-stone-500">Guarda la cotización para acceder a más acciones.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* CLIENTE Y EVENTO */}
          <div className="p-5 rounded-xl border" style={{ background: "var(--brand-surface-card)", borderColor: "var(--brand-border)" }} data-testid="card-cliente-evento">
            <h2 className="font-display text-lg mb-4">Cliente y evento</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs text-stone-600 mb-1 block">Cliente</label>
                <div className="flex gap-2">
                  <select value={clienteId} onChange={e=>setClienteId(e.target.value)} className="flex-1 min-w-0 px-3 py-2 rounded-lg border text-sm" data-testid="select-cliente">
                    <option value="">— Selecciona —</option>
                    {clientes.map(c => <option key={c.cliente_id} value={c.cliente_id}>{c.nombre}</option>)}
                  </select>
                  <button onClick={()=>setModalCliente(true)} className="px-3 py-2 rounded-lg border text-sm whitespace-nowrap shrink-0" data-testid="btn-nuevo-cliente"><Plus className="w-4 h-4 inline sm:mr-1" /><span className="hidden sm:inline">Nuevo cliente</span></button>
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-stone-600 mb-1 block">Presentar cotización como</label>
                {identidades.length === 0 ? (
                  <div className="px-3 py-2 rounded-lg border bg-stone-50 text-xs text-stone-500" data-testid="identidad-vacia">
                    Sin identidades configuradas. <a href="/panel/cotizador-config" className="underline">Configura una identidad comercial</a>.
                  </div>
                ) : (
                  <select value={identidadId} onChange={e=>setIdentidadId(e.target.value)} disabled={!!snapshotAt} className="w-full px-3 py-2 rounded-lg border text-sm disabled:opacity-60" data-testid="select-identidad">
                    {identidades.map(i => <option key={i.identidad_id} value={i.identidad_id}>{i.nombre_comercial}{i.es_default ? " (default)" : ""}</option>)}
                  </select>
                )}
              </div>
              <F label="Tipo de evento"><select value={tipoEvento} onChange={e=>setTipoEvento(e.target.value)} className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="input-tipo-evento">
                <option value="">— Selecciona —</option>
                {tiposEvento.map(t => <option key={t} value={t}>{t}</option>)}
              </select></F>
              <F label="Festejado / nombre del evento"><input value={festejado} onChange={e=>setFestejado(e.target.value)} className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="input-festejado" /></F>
              <F label="Fecha"><input type="date" value={fecha} onChange={e=>setFecha(e.target.value)} className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="input-fecha" /></F>
              <F label="Ubicación"><input value={ubicacion} onChange={e=>setUbicacion(e.target.value)} className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="input-ubicacion" /></F>
              <F label="Hora inicio"><input type="time" value={horaInicio} onChange={e=>setHoraInicio(e.target.value)} className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="input-hora-inicio" /></F>
              <F label="Hora fin"><input type="time" value={horaFin} onChange={e=>setHoraFin(e.target.value)} className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="input-hora-fin" /></F>
              <F label="Adultos"><input type="number" min="0" value={adultos} onChange={e=>setAdultos(e.target.value)} className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="input-adultos" /></F>
              <F label="Niños"><input type="number" min="0" value={ninos} onChange={e=>setNinos(e.target.value)} className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="input-ninos" /></F>
              <F label="Otros invitados"><input type="number" min="0" value={otros} onChange={e=>setOtros(e.target.value)} className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="input-otros" /></F>
              <F label="Total invitados">
                <div className="px-3 py-2 rounded-lg border bg-stone-50 text-sm font-semibold" data-testid="total-invitados">{Number(adultos||0)+Number(ninos||0)+Number(otros||0)}</div>
              </F>
            </div>
          </div>

          {/* CONSTRUCTOR */}
          <div className="p-5 rounded-xl border" style={{ background: "var(--brand-surface-card)", borderColor: "var(--brand-border)" }} data-testid="card-constructor">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h2 className="font-display text-lg">Constructor de cotización</h2>
              <button onClick={addSeccion} className="px-3 py-2 rounded-lg text-white text-sm font-medium" style={{ background: "var(--brand-primary)" }} data-testid="btn-agregar-seccion">
                <Plus className="w-4 h-4 inline mr-1" />Agregar sección
              </button>
            </div>
            {secciones.length === 0 && <p className="text-sm text-stone-500 py-6 text-center">Aún no hay secciones. Agrega la primera para armar tu cotización.</p>}
            <div className="space-y-3">
              {secciones.map((sec, si) => (
                <div key={sec.id} className="border rounded-lg overflow-hidden" data-testid={`seccion-${si}`}>
                  <div className="flex items-center gap-1.5 sm:gap-2 p-2.5 sm:p-3 bg-stone-50 flex-wrap sm:flex-nowrap">
                    <GripVertical className="w-4 h-4 text-stone-400 shrink-0" />
                    <input value={sec.nombre} onChange={e=>updateSeccion(si,{nombre:e.target.value})} className="flex-1 min-w-[120px] bg-transparent font-medium outline-none text-sm sm:text-base" data-testid={`seccion-nombre-${si}`} />
                    <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
                      <button onClick={()=>moveSeccion(si,-1)} className="p-1.5 hover:bg-white rounded" title="Subir"><ChevronUp className="w-4 h-4" /></button>
                      <button onClick={()=>moveSeccion(si,1)} className="p-1.5 hover:bg-white rounded" title="Bajar"><ChevronDown className="w-4 h-4" /></button>
                      <button onClick={()=>duplicarSeccion(si)} className="p-1.5 hover:bg-white rounded" title="Duplicar"><Copy className="w-4 h-4" /></button>
                      <button onClick={()=>updateSeccion(si,{expandido: !sec.expandido})} className="p-1.5 hover:bg-white rounded text-xs whitespace-nowrap">{sec.expandido?"Contraer":"Expandir"}</button>
                      <button onClick={()=>removeSeccion(si)} className="p-1.5 hover:bg-red-50 text-red-600 rounded" data-testid={`btn-del-seccion-${si}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  {sec.expandido !== false && (
                    <div className="p-3 space-y-2">
                      {(sec.items||[]).map((it, ci) => (
                        <div key={it.id||ci} className="flex flex-col sm:flex-row sm:items-start gap-2 p-2.5 rounded border bg-white" data-testid={`concepto-${si}-${ci}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap"><b className="text-sm">{it.nombre}</b>
                              <span className="text-[10px] px-1.5 py-0.5 rounded" style={{background:'#f5efe4',color:'#78350f'}}>{it.clasificacion||'incluido'}</span>
                            </div>
                            <div className="text-xs text-stone-500 break-words">{it.descripcion}</div>
                            <div className="text-xs text-stone-600 mt-1">{it.cantidad} {it.unidad} · {fmt(it.precio_unitario)} · <b>{fmt(it.importe)}</b></div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0 justify-end">
                            <button onClick={()=>setModalConcepto({si, ci})} className="p-1.5 rounded hover:bg-stone-100" data-testid={`btn-edit-concepto-${si}-${ci}`}><Edit3 className="w-4 h-4" /></button>
                            <button onClick={()=>duplicarConcepto(si,ci)} className="p-1.5 rounded hover:bg-stone-100"><Copy className="w-4 h-4" /></button>
                            <button onClick={()=>moverConcepto(si,ci,-1)} className="p-1.5 rounded hover:bg-stone-100"><ChevronUp className="w-4 h-4" /></button>
                            <button onClick={()=>moverConcepto(si,ci,1)} className="p-1.5 rounded hover:bg-stone-100"><ChevronDown className="w-4 h-4" /></button>
                            <button onClick={()=>eliminarConcepto(si,ci)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>
                      ))}
                      <div className="flex gap-2 flex-wrap pt-1">
                        <button onClick={()=>setModalConcepto({si})} className="text-xs px-3 py-1.5 rounded border hover:bg-stone-50" data-testid={`btn-agregar-concepto-${si}`}><Plus className="w-3 h-3 inline mr-1" />Agregar concepto</button>
                        <button onClick={()=>setModalCatalogo({si})} className="text-xs px-3 py-1.5 rounded border hover:bg-stone-50" data-testid={`btn-agregar-catalogo-${si}`}><Package className="w-3 h-3 inline mr-1" />Agregar desde Catálogo</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RESUMEN ECONÓMICO */}
        <div>
          <div className="p-5 rounded-xl border lg:sticky lg:top-24" style={{ background: "var(--brand-surface-card)", borderColor: "var(--brand-border)" }} data-testid="card-resumen">
            <h3 className="font-display text-lg mb-3">Resumen económico</h3>
            <div className="space-y-2 text-sm">
              <Row k="Subtotal" v={fmt(totales.subtotal)} tid="row-subtotal" />
              <div className="flex items-center justify-between">
                <span className="text-stone-600">Descuento</span>
                <input type="number" min="0" value={descuento} onChange={e=>setDescuento(e.target.value)} className="w-24 px-2 py-1 rounded border text-right text-sm" data-testid="input-descuento" />
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-stone-600"><input type="checkbox" checked={iva} onChange={e=>setIva(e.target.checked)} data-testid="check-iva" />IVA</label>
                <div className="flex items-center gap-1">
                  <input type="number" min="0" step="0.01" value={ivaTasa} onChange={e=>setIvaTasa(e.target.value)} className="w-14 px-2 py-1 rounded border text-right text-sm" disabled={!iva} />
                  <span className="text-xs text-stone-500">%</span>
                  <b className="ml-2" data-testid="iva-monto">{fmt(totales.ivaMonto)}</b>
                </div>
              </div>
              <div className="pt-2 border-t"><Row k="Total calculado" v={fmt(totales.totalCalc)} bold tid="row-total-calc" /></div>
              <label className="flex items-center gap-2 text-xs text-stone-600 pt-1"><input type="checkbox" checked={usarPactado} onChange={e=>setUsarPactado(e.target.checked)} data-testid="check-usar-pactado" />Usar total pactado</label>
              {usarPactado && (
                <div className="flex items-center justify-between">
                  <span className="text-stone-600">Total pactado</span>
                  <input type="number" min="0" value={totalPactado} onChange={e=>setTotalPactado(e.target.value)} className="w-32 px-2 py-1 rounded border text-right text-sm" data-testid="input-total-pactado" />
                </div>
              )}
              <div className="pt-2 border-t"><Row k="Total contratado" v={fmt(totales.totalContr)} bold big tid="row-total-contr" /></div>
              <div className="flex items-center justify-between">
                <span className="text-stone-600">Anticipo</span>
                <input type="number" min="0" value={anticipo} onChange={e=>setAnticipo(e.target.value)} className="w-24 px-2 py-1 rounded border text-right text-sm" />
              </div>
              <Row k="Pagado" v={fmt(anticipo)} />
              <div className="pt-2 border-t"><Row k="Saldo" v={fmt(totales.saldo)} bold /></div>
            </div>
          </div>
        </div>
      </div>

      {modalConcepto && <ModalConcepto data={modalConcepto} secciones={secciones} onClose={()=>setModalConcepto(null)} onSave={saveConcepto} />}
      {modalCatalogo && <ModalCatalogo catalogo={catalogo} onClose={()=>setModalCatalogo(null)} onSelect={(it)=>insertarDesdeCatalogo(modalCatalogo.si, it)} />}
      {modalCliente && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-5 max-w-md w-full">
            <h3 className="font-display text-lg mb-3">Nuevo cliente</h3>
            <input placeholder="Nombre *" value={nuevoCliNombre} onChange={e=>setNuevoCliNombre(e.target.value)} className="w-full px-3 py-2 rounded border mb-2" data-testid="input-nuevo-cli-nombre" />
            <input placeholder="Teléfono *" value={nuevoCliTelefono} onChange={e=>setNuevoCliTelefono(e.target.value)} className="w-full px-3 py-2 rounded border mb-3" data-testid="input-nuevo-cli-tel" />
            <div className="flex gap-2 justify-end">
              <button onClick={()=>setModalCliente(false)} className="px-3 py-2 rounded border text-sm">Cancelar</button>
              <button onClick={crearCliente} className="px-3 py-2 rounded text-white text-sm" style={{background:'var(--brand-primary)'}} data-testid="btn-crear-cli-modal">Crear</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const F = ({ label, children }) => (
  <div><label className="text-xs text-stone-600 mb-1 block">{label}</label>{children}</div>
);
const Row = ({ k, v, bold, big, tid }) => (
  <div className="flex items-center justify-between" data-testid={tid}>
    <span className={bold ? "font-semibold" : "text-stone-600"}>{k}</span>
    <span className={bold ? (big ? "font-display text-xl" : "font-semibold") : ""}>{v}</span>
  </div>
);

const ModalConcepto = ({ data, secciones, onClose, onSave }) => {
  const existing = data.ci != null ? secciones[data.si].items[data.ci] : null;
  const [c, setC] = useState(existing ? {...existing} : { nombre:"", descripcion:"", cantidad:1, unidad:"Servicio", modalidad:"cantidad_x_precio", precio_unitario:0, clasificacion:"incluido", seleccionado:true, unidad_custom:"" });
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl p-5 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg">{existing?"Editar concepto":"Nuevo concepto"}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-stone-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <F label="Concepto"><input value={c.nombre} onChange={e=>setC({...c,nombre:e.target.value})} className="w-full px-3 py-2 rounded border text-sm" data-testid="modal-concepto-nombre" /></F>
          <F label="Descripción"><textarea rows="2" value={c.descripcion} onChange={e=>setC({...c,descripcion:e.target.value})} className="w-full px-3 py-2 rounded border text-sm" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Cantidad"><input type="number" min="0" step="0.01" value={c.cantidad} onChange={e=>setC({...c,cantidad:e.target.value})} className="w-full px-3 py-2 rounded border text-sm" /></F>
            <F label="Unidad">
              <select value={c.unidad} onChange={e=>setC({...c,unidad:e.target.value})} className="w-full px-3 py-2 rounded border text-sm">
                {UNIDADES.map(u=><option key={u}>{u}</option>)}
              </select>
            </F>
            {c.unidad === "Otro" && <F label="Especificar unidad"><input value={c.unidad_custom||""} onChange={e=>setC({...c,unidad_custom:e.target.value})} className="w-full px-3 py-2 rounded border text-sm" /></F>}
            <F label="Modalidad de precio">
              <select value={c.modalidad} onChange={e=>setC({...c,modalidad:e.target.value})} className="w-full px-3 py-2 rounded border text-sm" data-testid="modal-modalidad">
                {MODALIDADES.map(m=><option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </F>
            {c.modalidad === "importe_manual"
              ? <F label="Importe"><input type="number" min="0" step="0.01" value={c.importe||0} onChange={e=>setC({...c,importe:e.target.value})} className="w-full px-3 py-2 rounded border text-sm" /></F>
              : <F label="Precio"><input type="number" min="0" step="0.01" value={c.precio_unitario} onChange={e=>setC({...c,precio_unitario:e.target.value})} className="w-full px-3 py-2 rounded border text-sm" data-testid="modal-precio" /></F>
            }
          </div>
          <F label="Clasificación">
            <select value={c.clasificacion} onChange={e=>setC({...c,clasificacion:e.target.value})} className="w-full px-3 py-2 rounded border text-sm">
              <option value="incluido">Incluido</option><option value="opcional">Opcional</option><option value="adicional">Adicional</option>
            </select>
          </F>
        </div>
        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onClose} className="px-3 py-2 rounded border text-sm">Cancelar</button>
          <button onClick={()=>onSave(data.si, c, data.ci)} className="px-3 py-2 rounded text-white text-sm" style={{background:'var(--brand-primary)'}} data-testid="modal-guardar-concepto">Guardar concepto</button>
        </div>
      </div>
    </div>
  );
};

const ModalCatalogo = ({ catalogo, onClose, onSelect }) => {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("todo");
  const CATS = [
    { key: "todo", label: "Todo" },
    { key: "experiencias", label: "Experiencias / Paquetes" },
    { key: "menus", label: "Menús" },
    { key: "servicios", label: "Servicios y extras" },
    { key: "otros", label: "Otros" },
  ];
  const filtrado = useMemo(() => catalogo.filter(c => {
    const cv = c.categoria_vista || "otros";
    if (cat === "experiencias" && !["experiencias", "paquetes"].includes(cv)) return false;
    if (cat !== "todo" && cat !== "experiencias" && cv !== cat) return false;
    if (q && !(c.nombre || "").toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [catalogo, q, cat]);
  const conteo = (k) => catalogo.filter(c => {
    const cv = c.categoria_vista || "otros";
    if (k === "todo") return true;
    if (k === "experiencias") return ["experiencias", "paquetes"].includes(cv);
    return cv === k;
  }).length;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl p-5 max-w-lg w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg">Catálogo</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-stone-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {CATS.map(c => {
            const active = cat === c.key;
            return (
              <button key={c.key} onClick={() => setCat(c.key)} className={`text-xs px-2.5 py-1.5 rounded-full font-medium transition-colors ${active ? "bg-rose-700 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`} data-testid={`cat-tab-${c.key}`}>
                {c.label} <span className={`ml-1 text-[10px] ${active ? "opacity-80" : "text-stone-500"}`}>{conteo(c.key)}</span>
              </button>
            );
          })}
        </div>
        <div className="relative mb-3"><Search className="w-4 h-4 absolute left-3 top-2.5 text-stone-400" /><input value={q} onChange={e=>setQ(e.target.value)} placeholder="Buscar…" className="w-full pl-9 pr-3 py-2 rounded border text-sm" data-testid="input-buscar-catalogo" /></div>
        <div className="flex-1 overflow-y-auto space-y-1.5">
          {filtrado.length===0 && <p className="text-sm text-stone-500 text-center py-6">Sin items</p>}
          {filtrado.map(it => (
            <button key={`${it._origen||"x"}-${it.catalogo_id||it.item_id}`} onClick={()=>onSelect(it)} className="w-full text-left p-3 rounded border hover:bg-stone-50" data-testid={`catalogo-item-${it.catalogo_id||it.item_id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2"><b className="text-sm">{it.nombre}</b><span className="text-[10px] uppercase tracking-wide bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded">{it.categoria_vista||"otros"}</span></div>
                  <div className="text-xs text-stone-500 truncate">{it.descripcion||"—"}</div>
                </div>
                <span className="text-sm">{fmt(it.precio||it.precio_unitario||0)}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NuevaCotizacion;
